/**
 * Kullanıcı Aktivite Takip Hook'u - DETAYLI VERSİYON
 * Müşterilerin paneldeki TÜM hareketlerini otomatik kaydeder
 * 
 * Takip edilenler:
 * - Arama (kelime, sonuç sayısı, bulundu mu)
 * - Ürün görüntüleme (SKU, süre)
 * - Sayfa gezinme (URL, süre)
 * - Sepet işlemleri
 */
import { useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/panel`;

// 🛡️ GÜVENLİ LOCALSTORAGE FONKSİYONLARI
// Quota hatası durumunda eski verileri temizler
const safeLocalStorage = {
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      // Quota aşıldı - eski aktivite verilerini temizle
      if (e.name === 'QuotaExceededError' || e.code === 22) {
        console.warn('⚠️ LocalStorage quota aşıldı, eski veriler temizleniyor...');
        // Aktivite ile ilgili eski verileri temizle
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && (
            k.startsWith('arama_cache_') ||
            k.startsWith('teklif_gecmisi_') ||
            k.startsWith('urun_listesi_') ||
            k.startsWith('aktivite_') ||
            k === 'oturum_id'
          )) {
            keysToRemove.push(k);
          }
        }
        keysToRemove.forEach(k => {
          try { localStorage.removeItem(k); } catch (err) {}
        });
        // Tekrar dene
        try {
          localStorage.setItem(key, value);
        } catch (err) {
          // Hala başarısız - sessizce devam et
          console.warn('LocalStorage yazılamadı:', key);
        }
      }
    }
  },
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {}
  }
};

// otomatik=false → yalnız kayıt fonksiyonlarını döndürür; sayfa süresi / oturum / sekme / pano takibini
// başlatmaz. Sol menü bunu yalnız öndeki iç sekmede açar: TabLayout her iç sekmeyi bağlı tuttuğu için
// N sekme = N kopya, her sayfa geçişinde N kez sayfa-suresi-kaydet gidiyordu (2026-10-23).
export const useAktiviteTakip = ({ otomatik = true } = {}) => {
  const { user } = useAuth();
  const location = useLocation();
  const sayfaBaslangicRef = useRef(null);
  const oncekiSayfaRef = useRef(null);
  const urunGoruntulemeSureRef = useRef({});
  
  // Tüm panel kullanıcıları için aktif (müşteri, firma, admin vs.)
  // Sadece belirli roller hariç tutulabilir
  const isAktif = user && ['musteri', 'client', 'firma', 'mudur', 'admin', 'superadmin'].includes(user?.role);
  
  // 🆕 Müşteri kontrolü - stok adetleri gösterilmemeli
  const isMusteri = user?.role === 'client' || user?.role === 'musteri' || user?.role === 'Müşteri';
  
  // Kullanıcı bilgilerini al
  const getUserInfo = useCallback(() => ({
    user_id: user?.id || user?._id || user?.user_id || localStorage.getItem('userId'),
    email: user?.email || localStorage.getItem('email'),
    firma: user?.firma || localStorage.getItem('firma') || 'OTOMOTIV',
    panel_kodu: user?.panel_kodu || localStorage.getItem('panel_kodu'),
    cari_unvan: user?.cari_unvan || localStorage.getItem('cari_unvan'),
    role: user?.role
  }), [user]);

  // ==================== ARAMA TAKİBİ ====================
  
  /**
   * Arama aktivitesi kaydet - DETAYLI
   * @param {string} aramaKelime - Aranan kelime
   * @param {number} sonucSayisi - Bulunan sonuç sayısı
   * @param {Array} bulunanSkular - Bulunan ürün SKU'ları (ilk 10)
   * @param {string} aramaTipi - 'genel', 'sku', 'oem', 'olcu'
   */
  const aramaKaydet = useCallback(async (aramaKelime, sonucSayisi = 0, bulunanSkular = [], aramaTipi = 'genel') => {
    if (!isAktif || !user || !aramaKelime || aramaKelime.length < 2) return;
    
    try {
      await axios.post(`${API}/arama-kaydet`, {
        ...getUserInfo(),
        arama_kelime: aramaKelime,
        sonuc_sayisi: sonucSayisi,
        sonuc_bulundu: sonucSayisi > 0,
        arama_tipi: aramaTipi,
        bulunan_skular: bulunanSkular.slice(0, 10)
      });
      console.log('[Aktivite] Arama kaydedildi:', aramaKelime, '- Sonuç:', sonucSayisi);
    } catch (err) {
      console.log('[Aktivite] Arama kaydedilemedi:', err.message);
    }
  }, [isAktif, user, getUserInfo]);

  // ==================== ÜRÜN GÖRÜNTÜLEME TAKİBİ ====================
  
  /**
   * Ürün görüntüleme başlat (modal açıldığında)
   */
  const urunGoruntulemeBaslat = useCallback((urunSku) => {
    if (!isAktif) return;
    urunGoruntulemeSureRef.current[urunSku] = Date.now();
    console.log('[Aktivite] Ürün görüntüleme başladı:', urunSku);
  }, [isAktif]);

  /**
   * Ürün görüntüleme bitir ve kaydet (modal kapandığında)
   */
  const urunGoruntulemeBitir = useCallback(async (urunSku, urunAdi, fiyat = 0, sepeteEklendi = false) => {
    if (!isAktif || !user) return;
    
    const baslangic = urunGoruntulemeSureRef.current[urunSku];
    const sureSn = baslangic ? Math.round((Date.now() - baslangic) / 1000) : 0;
    
    delete urunGoruntulemeSureRef.current[urunSku];
    
    try {
      await axios.post(`${API}/urun-goruntuleme-kaydet`, {
        ...getUserInfo(),
        urun_sku: urunSku,
        urun_adi: urunAdi,
        sure_sn: sureSn,
        fiyat: fiyat,
        sepete_eklendi: sepeteEklendi
      });
      console.log('[Aktivite] Ürün görüntüleme kaydedildi:', urunSku, '- Süre:', sureSn, 'sn');
    } catch (err) {
      console.log('[Aktivite] Ürün görüntüleme kaydedilemedi:', err.message);
    }
  }, [isAktif, user, getUserInfo]);

  /**
   * Basit ürün görüntüleme kaydet (anlık)
   */
  const urunGoruntulemeKaydet = useCallback(async (urunSku, urunAdi, fiyat = 0) => {
    if (!isAktif || !user) return;
    
    try {
      await axios.post(`${API}/urun-goruntuleme-kaydet`, {
        ...getUserInfo(),
        urun_sku: urunSku,
        urun_adi: urunAdi,
        sure_sn: 0,
        fiyat: fiyat,
        sepete_eklendi: false
      });
    } catch (err) {
      console.log('[Aktivite] Ürün görüntüleme kaydedilemedi:', err.message);
    }
  }, [isAktif, user, getUserInfo]);

  // ==================== SEPET TAKİBİ ====================
  
  /**
   * Sepete ekleme kaydet
   */
  const sepetEklemeKaydet = useCallback(async (urunSku, urunAdi, miktar, tutar) => {
    if (!isAktif || !user) return;
    
    try {
      await axios.post(`${API}/aktivite-kaydet`, {
        ...getUserInfo(),
        aktivite_tipi: 'sepet_ekleme',
        urun_sku: urunSku,
        urun_adi: urunAdi,
        miktar: miktar,
        tutar: tutar
      });
      console.log('[Aktivite] Sepet ekleme kaydedildi:', urunSku);
    } catch (err) {
      console.log('[Aktivite] Sepet ekleme kaydedilemedi:', err.message);
    }
  }, [isAktif, user, getUserInfo]);

  /**
   * Sepetten çıkarma kaydet
   */
  const sepetCikarmaKaydet = useCallback(async (urunSku, urunAdi, miktar) => {
    if (!isAktif || !user) return;
    
    try {
      await axios.post(`${API}/aktivite-kaydet`, {
        ...getUserInfo(),
        aktivite_tipi: 'sepet_cikarma',
        urun_sku: urunSku,
        urun_adi: urunAdi,
        miktar: miktar
      });
      console.log('[Aktivite] Sepet çıkarma kaydedildi:', urunSku);
    } catch (err) {
      console.log('[Aktivite] Sepet çıkarma kaydedilemedi:', err.message);
    }
  }, [isAktif, user, getUserInfo]);

  // ==================== SİPARİŞ TAKİBİ ====================
  
  /**
   * Sipariş kaydet
   */
  const siparisKaydet = useCallback(async (siparisNo, toplamTutar, urunSayisi, odemeTipi) => {
    if (!isAktif || !user) return;
    
    try {
      await axios.post(`${API}/aktivite-kaydet`, {
        ...getUserInfo(),
        aktivite_tipi: 'siparis',
        siparis_no: siparisNo,
        toplam_tutar: toplamTutar,
        urun_sayisi: urunSayisi,
        odeme_tipi: odemeTipi
      });
      console.log('[Aktivite] Sipariş kaydedildi:', siparisNo);
    } catch (err) {
      console.log('[Aktivite] Sipariş kaydedilemedi:', err.message);
    }
  }, [isAktif, user, getUserInfo]);

  // ==================== SAYFA GEZİNME TAKİBİ ====================
  
  // Sayfa adı helper - useEffect'ten önce tanımlanmalı
  const getSayfaAdi = (pathname) => {
    const sayfaMap = {
      '/panel/anasayfa': 'Ana Sayfa',
      '/panel/urunler': 'Ürün Kataloğu',
      '/panel/sepetim': 'Sepetim',
      '/panel/siparislerim': 'Siparişlerim',
      '/panel/musteri-cari': 'Cari Hesabım',
      '/panel/uyelik': 'Üyelik Bilgileri',
      '/panel/adreslerim': 'Adreslerim'
    };
    return sayfaMap[pathname] || pathname;
  };

  // Sayfa değişikliğinde önceki sayfanın süresini kaydet
  useEffect(() => {
    if (!isAktif || !otomatik || !user) {
      // arkadaki iç sekmede süre biriktirme: öne gelince bekleme süresi sayfa süresi sayılmasın
      oncekiSayfaRef.current = null;
      sayfaBaslangicRef.current = null;
      return;
    }
    
    const kaydedilecekSayfa = oncekiSayfaRef.current;
    const baslangicZamani = sayfaBaslangicRef.current;
    
    // Önceki sayfanın süresini kaydet
    if (kaydedilecekSayfa && baslangicZamani) {
      const kalmaSuresi = Math.round((Date.now() - baslangicZamani) / 1000);
      
      // Minimum 2 saniye kalmışsa kaydet
      if (kalmaSuresi >= 2) {
        axios.post(`${API}/sayfa-suresi-kaydet`, {
          ...getUserInfo(),
          sayfa_url: kaydedilecekSayfa,
          sayfa_adi: getSayfaAdi(kaydedilecekSayfa),
          kalma_suresi_sn: kalmaSuresi
        }).catch(() => {});
        
        console.log('[Aktivite] Sayfa süresi kaydedildi:', kaydedilecekSayfa, '-', kalmaSuresi, 'sn');
      }
    }
    
    // Yeni sayfa için zamanlayıcı başlat
    oncekiSayfaRef.current = location.pathname;
    sayfaBaslangicRef.current = Date.now();
    
  }, [location.pathname, isAktif, otomatik, user, getUserInfo]);

  // ==================== OTURUM YÖNETİMİ ====================
  
  /**
   * Oturum başlat (giriş yapıldığında)
   */
  const oturumBaslat = useCallback(async () => {
    if (!isAktif || !user) return;
    
    try {
      await axios.post(`${API}/oturum-baslat`, getUserInfo());
      console.log('[Aktivite] Oturum başlatıldı');
    } catch (err) {
      console.log('[Aktivite] Oturum başlatılamadı:', err.message);
    }
  }, [isAktif, user, getUserInfo]);

  /**
   * Oturum bitir (çıkış yapıldığında)
   */
  const oturumBitir = useCallback(async () => {
    if (!user) return;
    
    try {
      await axios.post(`${API}/oturum-bitir`, {
        user_id: user?.id || user?._id || user?.user_id || localStorage.getItem('userId')
      });
      console.log('[Aktivite] Oturum bitirildi');
    } catch (err) {
      console.log('[Aktivite] Oturum bitirilemedi:', err.message);
    }
  }, [user]);

  // ==================== İSTİHBARAT TAKİBİ ====================
  
  // Son arama bilgisi (tab değişikliğinde kullanmak için)
  const sonAramaRef = useRef({ kelime: '', sonucVar: false, zaman: 0 });
  // 🆕 Son bakılan ürün bilgisi (tab değişikliğinde ürün bazlı takip için)
  const sonUrunRef = useRef({ sku: '', urun_adi: '', fiyat: 0, zaman: 0 });
  const tabKayipZamaniRef = useRef(null);
  
  /**
   * Son arama bilgisini güncelle (her aramada çağrılmalı)
   */
  const sonAramaGuncelle = useCallback((kelime, sonucVar) => {
    sonAramaRef.current = {
      kelime,
      sonucVar,
      zaman: Date.now()
    };
  }, []);

  /**
   * 🆕 Son bakılan ürün bilgisini güncelle (ürün detay açıldığında çağrılmalı)
   */
  const sonUrunGuncelle = useCallback((sku, urun_adi, fiyat = 0) => {
    sonUrunRef.current = {
      sku,
      urun_adi,
      fiyat,
      zaman: Date.now()
    };
  }, []);

  /**
   * Tab focus değişikliği - Rakip analizi için kritik!
   */
  useEffect(() => {
    if (!isAktif || !otomatik || !user) return;
    
    const handleVisibilityChange = () => {
      const userInfo = getUserInfo();
      
      if (document.hidden) {
        // Tab focus kaybetti - başka sekmeye geçti (rakibe mi gidiyor?)
        tabKayipZamaniRef.current = Date.now();
        
        // Son arama 30 saniye içinde yapıldıysa kaydet
        const sonArama = sonAramaRef.current;
        const sonUrun = sonUrunRef.current;
        const aramaSuresi = Date.now() - sonArama.zaman;
        const urunSuresi = Date.now() - sonUrun.zaman;
        
        // 🆕 Ürün veya arama bilgisi varsa kaydet
        const kayitData = {
          ...userInfo,
          durum: 'focus_kaybetti',
          onceki_sayfa: location.pathname
        };
        
        // Son 60 saniye içinde ürün görüntülendiyse ekle
        if (sonUrun.sku && urunSuresi < 60000) {
          kayitData.son_urun_sku = sonUrun.sku;
          kayitData.son_urun_adi = sonUrun.urun_adi;
          kayitData.son_urun_fiyat = sonUrun.fiyat;
        }
        
        // Son 30 saniye içinde arama yapıldıysa ekle
        if (sonArama.kelime && aramaSuresi < 30000) {
          kayitData.son_arama = sonArama.kelime;
          kayitData.son_arama_sonuc_var = sonArama.sonucVar;
        }
        
        // En az bir bilgi varsa kaydet
        if (kayitData.son_urun_sku || kayitData.son_arama) {
          axios.post(`${BACKEND_URL}/api/panel/istihbarat/tab-degisikligi`, kayitData).catch(() => {});
          console.log('[İstihbarat] Tab focus kaybetti - Ürün:', sonUrun.sku || '-', '- Arama:', sonArama.kelime || '-');
        }
      } else {
        // Tab focus kazandı - geri döndü
        const kayipSuresi = tabKayipZamaniRef.current ? Math.round((Date.now() - tabKayipZamaniRef.current) / 1000) : 0;
        const sonUrun = sonUrunRef.current;
        
        if (kayipSuresi > 2) {  // En az 2 saniye uzakta kaldıysa kaydet
          const kayitData = {
            ...userInfo,
            durum: 'focus_kazandi',
            kayip_suresi_sn: kayipSuresi,
            onceki_sayfa: location.pathname
          };
          
          // 🆕 Son bakılan ürün bilgisini de ekle (geri döndüğünde hangi ürüne bakıyordu)
          if (sonUrun.sku) {
            kayitData.son_urun_sku = sonUrun.sku;
            kayitData.son_urun_adi = sonUrun.urun_adi;
            kayitData.son_urun_fiyat = sonUrun.fiyat;
          }
          
          axios.post(`${BACKEND_URL}/api/panel/istihbarat/tab-degisikligi`, kayitData).catch(() => {});
          console.log('[İstihbarat] Tab focus kazandı - Uzakta:', kayipSuresi, 'sn - Ürün:', sonUrun.sku || '-');
        }
        
        tabKayipZamaniRef.current = null;
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAktif, otomatik, user, location.pathname, getUserInfo]);

  /**
   * Clipboard takibi - Kopyalama/Yapıştırma
   * Kopyalama: Müşteri SKU/OE'yi rakipte aramak için kopyaladı mı?
   * Yapıştırma: Rakipten kopyaladığı bir şeyi bizde arıyor mu? (geri döndü!)
   */
  useEffect(() => {
    if (!isAktif || !otomatik || !user) return;
    
    const userInfo = getUserInfo();
    
    // Kopyalama eventi
    const handleCopy = (e) => {
      const selectedText = window.getSelection()?.toString()?.trim();
      if (!selectedText || selectedText.length < 3 || selectedText.length > 50) return;
      
      // İçerik tipini tahmin et
      let icerikTipi = 'metin';
      if (/^[A-Z0-9\-]{4,20}$/i.test(selectedText)) {
        icerikTipi = 'sku';
      } else if (/^[A-Z0-9\s\-\.]{6,30}$/i.test(selectedText)) {
        icerikTipi = 'oem';
      }
      
      axios.post(`${BACKEND_URL}/api/panel/istihbarat/clipboard`, {
        ...userInfo,
        islem: 'kopyalama',
        icerik: selectedText,
        icerik_tipi: icerikTipi,
        kaynak_sayfa: location.pathname
      }).catch(() => {});
      
      console.log('[İstihbarat] Kopyalama:', selectedText, '- Tip:', icerikTipi);
    };
    
    // Yapıştırma eventi (arama kutusunda)
    const handlePaste = (e) => {
      // Sadece input ve textarea'larda
      if (!['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
      
      const pastedText = e.clipboardData?.getData('text')?.trim();
      if (!pastedText || pastedText.length < 3 || pastedText.length > 50) return;
      
      // İçerik tipini tahmin et
      let icerikTipi = 'metin';
      if (/^[A-Z0-9\-]{4,20}$/i.test(pastedText)) {
        icerikTipi = 'sku';
      } else if (/^[A-Z0-9\s\-\.]{6,30}$/i.test(pastedText)) {
        icerikTipi = 'oem';
      }
      
      axios.post(`${BACKEND_URL}/api/panel/istihbarat/clipboard`, {
        ...userInfo,
        islem: 'yapistirma',
        icerik: pastedText,
        icerik_tipi: icerikTipi,
        kaynak_sayfa: location.pathname
      }).catch(() => {});
      
      console.log('[İstihbarat] Yapıştırma (DÖNÜŞ SİNYALİ!):', pastedText, '- Tip:', icerikTipi);
    };
    
    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);
    
    return () => {
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
    };
  }, [isAktif, otomatik, user, location.pathname, getUserInfo]);

  /**
   * Detaylı arama kaydı - Dönüşüm hunisi için
   */
  const aramaDetayKaydet = useCallback(async (aramaData) => {
    if (!isAktif || !user) return;
    
    try {
      const userInfo = getUserInfo();
      
      await axios.post(`${BACKEND_URL}/api/panel/istihbarat/arama-detay`, {
        ...userInfo,
        ...aramaData,
        oturum_id: safeLocalStorage.getItem('oturum_id')
      });
      
      // Son arama bilgisini güncelle (tab takibi için)
      sonAramaGuncelle(aramaData.arama_kelime, aramaData.sonuc_sayisi > 0);
      
      console.log('[İstihbarat] Arama detay kaydedildi:', aramaData.arama_kelime);
    } catch (err) {
      console.log('[İstihbarat] Arama detay kaydedilemedi:', err.message);
    }
  }, [isAktif, user, getUserInfo, sonAramaGuncelle]);

  /**
   * Dönüşüm adımı kaydı
   */
  const donusumAdimiKaydet = useCallback(async (adimTipi, urunData = {}, ekstraData = {}) => {
    if (!isAktif || !user) return;
    
    try {
      const userInfo = getUserInfo();
      
      await axios.post(`${BACKEND_URL}/api/panel/istihbarat/donusum-adimi`, {
        ...userInfo,
        adim_tipi: adimTipi,
        urun_sku: urunData.sku,
        urun_adi: urunData.urun_adi,
        fiyat: urunData.fiyat,
        miktar: urunData.miktar,
        kaynak_arama: sonAramaRef.current.kelime,
        oturum_id: safeLocalStorage.getItem('oturum_id'),
        ...ekstraData
      });
      
      console.log('[İstihbarat] Dönüşüm adımı:', adimTipi, '-', urunData.sku || '');
    } catch (err) {
      console.log('[İstihbarat] Dönüşüm adımı kaydedilemedi:', err.message);
    }
  }, [isAktif, user, getUserInfo]);

  // Component mount olduğunda oturum başlat
  useEffect(() => {
    if (isAktif && otomatik && user) {
      oturumBaslat();
      
      // Oturum ID oluştur (tab takibi için) - GÜVENLİ
      if (!safeLocalStorage.getItem('oturum_id')) {
        safeLocalStorage.setItem('oturum_id', `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
      }
      
      // Sayfa kapanırken oturumu bitir
      const handleBeforeUnload = () => {
        // Son sayfa süresini kaydet
        if (oncekiSayfaRef.current && sayfaBaslangicRef.current) {
          const kalmaSuresi = Math.round((Date.now() - sayfaBaslangicRef.current) / 1000);
          navigator.sendBeacon(`${API}/sayfa-suresi-kaydet`, JSON.stringify({
            ...getUserInfo(),
            sayfa_url: oncekiSayfaRef.current,
            kalma_suresi_sn: kalmaSuresi
          }));
        }
        
        // Oturumu bitir
        navigator.sendBeacon(`${API}/oturum-bitir`, JSON.stringify({
          user_id: user?.id || user?._id || user?.user_id
        }));
        
        // Oturum ID temizle - GÜVENLİ
        safeLocalStorage.removeItem('oturum_id');
      };
      
      window.addEventListener('beforeunload', handleBeforeUnload);
      
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [isAktif, otomatik, user, oturumBaslat, getUserInfo]);

  return {
    // Arama
    aramaKaydet,
    
    // Ürün görüntüleme
    urunGoruntulemeBaslat,
    urunGoruntulemeBitir,
    urunGoruntulemeKaydet,
    
    // Sepet
    sepetEklemeKaydet,
    sepetCikarmaKaydet,
    
    // Sipariş
    siparisKaydet,
    
    // Oturum
    oturumBaslat,
    oturumBitir,
    
    // 🆕 İstihbarat
    aramaDetayKaydet,
    donusumAdimiKaydet,
    sonAramaGuncelle,
    sonUrunGuncelle,  // 🆕 Ürün bazlı tab takibi için
    
    // Durum
    isAktif,
    isMusteri  // 🆕 Stok gizliliği için
  };
};

export default useAktiviteTakip;
